"use client";

import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ServerIcon, OnlineIcon } from "@/components/icons";
import Link from "next/link";

// ─── Types ────────────────────────────────────────────────────────────────────

type PowerState = "running" | "stopped" | "unknown";
type ProvisionStatus = "pending" | "active" | "error" | "deprovisioned";

interface ProvisionedServer {
  vultrInstanceId: string;
  subscriptionTier: string;
  provisionStatus: ProvisionStatus;
  powerState: PowerState;
  powerStateCheckedAt: number | null;
  serverIp: string | null;
  serverIpv6: string | null;
  gamePort: number;
  rconPort: number;
  rconPassword: string | null;
  gameOnline: boolean | null;
  gamePingedAt: number | null;
  vultrRegion: string;
  vultrPlanSku: string;
  provisionedAt: number;
  activatedAt: number | null;
  lastCheckedAt: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const REGION_LABELS: Record<string, string> = {
  ewr: "New Jersey",
  lax: "Los Angeles",
  ord: "Chicago",
  dfw: "Dallas",
  sea: "Seattle",
  atl: "Atlanta",
  ams: "Amsterdam",
  lhr: "London",
  fra: "Frankfurt",
  sgp: "Singapore",
  nrt: "Tokyo",
  syd: "Sydney",
  sao: "São Paulo",
  icn: "Seoul",
  mia: "Miami",
  yto: "Toronto",
  cdg: "Paris",
  waw: "Warsaw",
  bom: "Mumbai",
};

function regionLabel(slug: string): string {
  return REGION_LABELS[slug] ?? slug.toUpperCase();
}

function formatDateShort(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <button
      onClick={handleCopy}
      title={`Copy ${label ?? "value"}`}
      className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium
        bg-foreground/5 text-muted hover:bg-foreground/10 hover:text-foreground
        transition-colors duration-100 select-none"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

// ─── Power state badge ────────────────────────────────────────────────────────

function PowerStateBadge({ state }: { state: PowerState }) {
  if (state === "running") {
    return (
      <Badge variant="success" className="gap-1">
        <OnlineIcon className="h-3 w-3" />
        Running
      </Badge>
    );
  }
  if (state === "stopped") {
    return <Badge variant="destructive">Stopped</Badge>;
  }
  return <Badge variant="outline">Unknown</Badge>;
}

// ─── Provision status badge ───────────────────────────────────────────────────

function ProvisionBadge({ status }: { status: ProvisionStatus }) {
  if (status === "active") return <Badge variant="success">Active</Badge>;
  if (status === "pending") return <Badge variant="secondary">Provisioning</Badge>;
  if (status === "error") return <Badge variant="destructive">Error</Badge>;
  return <Badge variant="outline">Deprovisioned</Badge>;
}

// ─── Game online / offline indicator ─────────────────────────────────────────

function GameOnlineBadge({
  gameOnline,
  gamePingedAt,
}: {
  gameOnline: boolean | null;
  gamePingedAt: number | null;
}) {
  if (gameOnline === null) {
    return (
      <Badge variant="outline" className="text-xs">
        Not checked
      </Badge>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {gameOnline ? (
        <>
          <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
          <span className="font-medium text-green-700 dark:text-green-400">Online</span>
        </>
      ) : (
        <>
          <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
          <span className="font-medium text-red-700 dark:text-red-400">Offline</span>
        </>
      )}
      {gamePingedAt && (
        <span className="text-muted">· {formatTimeAgo(gamePingedAt)}</span>
      )}
    </span>
  );
}

// ─── Single server card ───────────────────────────────────────────────────────

function ServerCard({ server }: { server: ProvisionedServer }) {
  const powerControl = useAction(api.vultr.serverPowerControl);
  const pingServer = useAction(api.vultr.pingMyGameServer);
  const [pending, setPending] = useState<"start" | "stop" | "reboot" | null>(null);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState<boolean | null>(null);
  const [pingTime, setPingTime] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [showRcon, setShowRcon] = useState(false);

  const isActive = server.provisionStatus === "active";

  async function handlePowerOp(op: "start" | "stop" | "reboot") {
    if (pending) return;
    setError("");
    setPending(op);
    try {
      await powerControl({
        vultrInstanceId: server.vultrInstanceId,
        operation: op,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Operation failed");
    } finally {
      setPending(null);
    }
  }

  async function handlePing() {
    if (pinging) return;
    setError("");
    setPinging(true);
    try {
      const result = await pingServer({ vultrInstanceId: server.vultrInstanceId });
      setPingResult(result.online);
      setPingTime(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ping failed");
    } finally {
      setPinging(false);
    }
  }

  // Derive the display game online state — prefer live ping result over stored
  const displayOnline = pingResult !== null ? pingResult : server.gameOnline;
  const displayPingTime = pingTime ?? server.gamePingedAt;

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="font-semibold text-foreground text-sm">
            {server.subscriptionTier.charAt(0).toUpperCase() +
              server.subscriptionTier.slice(1)}{" "}
            Server
          </p>
          <p className="text-xs text-muted font-mono">
            {server.vultrInstanceId.slice(0, 18)}…
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ProvisionBadge status={server.provisionStatus} />
          {isActive && <PowerStateBadge state={server.powerState} />}
        </div>
      </div>

      {/* Server connection info grid */}
      <div className="rounded-md border border-border bg-foreground/[0.02] p-3 space-y-2.5">
        <p className="text-xs font-semibold text-muted uppercase tracking-wide">
          Connection Details
        </p>

        {/* IP Address */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">IP Address</span>
          <span className="flex items-center font-mono text-foreground">
            {server.serverIp ?? (
              <span className="text-muted italic">Pending…</span>
            )}
            {server.serverIp && <CopyButton value={server.serverIp} label="IP" />}
          </span>
        </div>

        {/* Game Port */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Game Port</span>
          <span className="flex items-center font-mono text-foreground">
            {server.gamePort}
            <CopyButton value={String(server.gamePort)} label="port" />
          </span>
        </div>

        {/* Full connection string */}
        {server.serverIp && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted">Connect String</span>
            <span className="flex items-center font-mono text-foreground">
              {server.serverIp}:{server.gamePort}
              <CopyButton
                value={`${server.serverIp}:${server.gamePort}`}
                label="connection string"
              />
            </span>
          </div>
        )}

        {/* RCON Section */}
        <div className="pt-1 border-t border-border">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-muted font-semibold">RCON Access</span>
            <button
              onClick={() => setShowRcon((v) => !v)}
              className="text-xs text-primary hover:underline"
            >
              {showRcon ? "Hide" : "Show"}
            </button>
          </div>

          {showRcon && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted">RCON Host</span>
                <span className="flex items-center font-mono text-foreground">
                  {server.serverIp ?? <span className="text-muted italic">Pending…</span>}
                  {server.serverIp && (
                    <CopyButton value={server.serverIp} label="RCON host" />
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted">RCON Port</span>
                <span className="flex items-center font-mono text-foreground">
                  {server.rconPort}
                  <CopyButton value={String(server.rconPort)} label="RCON port" />
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted">RCON Password</span>
                <span className="flex items-center font-mono text-foreground">
                  {server.rconPassword ?? (
                    <span className="text-muted italic">Not set</span>
                  )}
                  {server.rconPassword && (
                    <CopyButton value={server.rconPassword} label="RCON password" />
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Server info grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div>
          <span className="text-muted">Region</span>
          <p className="text-foreground">{regionLabel(server.vultrRegion)}</p>
        </div>
        <div>
          <span className="text-muted">Plan</span>
          <p className="font-mono text-foreground">{server.vultrPlanSku}</p>
        </div>
        <div>
          <span className="text-muted">Provisioned</span>
          <p className="text-foreground">{formatDateShort(server.provisionedAt)}</p>
        </div>
        {server.powerStateCheckedAt && (
          <div>
            <span className="text-muted">Power checked</span>
            <p className="text-foreground">{formatTimeAgo(server.powerStateCheckedAt)}</p>
          </div>
        )}
      </div>

      {/* Game health ping section */}
      {isActive && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
          <div>
            <p className="text-xs font-medium text-foreground mb-0.5">Game Server Status</p>
            <GameOnlineBadge
              gameOnline={displayOnline}
              gamePingedAt={displayPingTime}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handlePing}
            disabled={pinging || !server.serverIp}
            className="shrink-0"
          >
            {pinging ? "Checking…" : "Ping Server"}
          </Button>
        </div>
      )}

      {/* Power controls — only shown when provisioned and active */}
      {isActive && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePowerOp("start")}
            disabled={!!pending || server.powerState === "running"}
          >
            {pending === "start" ? "Starting…" : "Start"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePowerOp("stop")}
            disabled={!!pending || server.powerState === "stopped"}
          >
            {pending === "stop" ? "Stopping…" : "Stop"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handlePowerOp("reboot")}
            disabled={!!pending || server.powerState !== "running"}
          >
            {pending === "reboot" ? "Rebooting…" : "Reboot"}
          </Button>
        </div>
      )}

      {/* Provisioning info when not yet active */}
      {server.provisionStatus === "pending" && (
        <p className="text-xs text-muted">
          Your server is being provisioned. This usually takes 1–3 minutes.
        </p>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

// ─── Provisioning skeleton card ───────────────────────────────────────────────

function ProvisioningSkeletonCard({ tier }: { tier: string }) {
  const tierName =
    tier === "starter"
      ? "Founder Starter"
      : tier === "plus"
        ? "Founder Plus"
        : tier === "pro"
          ? "Founder Pro"
          : tier === "enterprise"
            ? "Enterprise"
            : tier.charAt(0).toUpperCase() + tier.slice(1);

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-4 dark:border-blue-800 dark:bg-blue-950/20">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="font-semibold text-foreground text-sm">{tierName} Server</p>
          <p className="text-xs text-muted">Your server is being provisioned…</p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          Provisioning
        </Badge>
      </div>

      {/* Animated skeleton connection block */}
      <div className="rounded-md border border-border bg-foreground/[0.02] p-3 space-y-2.5">
        <p className="text-xs font-semibold text-muted uppercase tracking-wide">
          Connection Details
        </p>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">IP Address</span>
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Game Port</span>
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Connect String</span>
          <Skeleton className="h-4 w-36" />
        </div>
      </div>

      <p className="text-xs text-blue-700 dark:text-blue-400">
        Your dedicated game server is spinning up. This usually takes 1–5 minutes.
        IP and connection details will appear here automatically once ready.
      </p>
    </div>
  );
}

// ─── Main exported panel ──────────────────────────────────────────────────────

/**
 * VultrServerPanel
 *
 * Dashboard panel showing all Vultr-provisioned game servers for the
 * authenticated user. Displays:
 * - Server connection details: IP, game port, full connect string (with copy buttons)
 * - RCON credentials (show/hide toggle with copy buttons)
 * - Live power state (VM-level, from Vultr API via cron)
 * - Game-level health ping indicator (user-triggered)
 * - Power controls: start / stop / reboot
 * - "Provisioning in progress" skeleton when subscription is active but server not yet visible
 *
 * Renders whenever the user has an active subscription, even during provisioning.
 */
export function VultrServerPanel() {
  const servers = useQuery(api.vultr.getMyProvisionedServersWithPower);
  const accessRecords = useQuery(api.serverAccess.getMyServerAccess);

  const isLoading = servers === undefined || accessRecords === undefined;

  // Determine if there's an active (or trialing) subscription with no server yet
  const activeAccess = accessRecords?.find((r) => r.status === "active") ?? null;
  const hasActiveSubscription = activeAccess !== null;
  const hasServers = (servers?.length ?? 0) > 0;

  // Nothing to show — no subscription, no servers
  if (!isLoading && !hasActiveSubscription && !hasServers) return null;

  return (
    <Card className="mb-8">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <ServerIcon className="h-4 w-4" />
          Game Servers
        </CardTitle>
        {hasServers && (
          <Link
            href="/dashboard/servers"
            className="text-xs text-muted hover:text-foreground hover:underline transition-colors"
          >
            Full details →
          </Link>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Query loading skeleton */}
        {isLoading && <Skeleton className="h-28" />}

        {/* Active server cards */}
        {!isLoading &&
          servers!.map((server) => (
            <ServerCard key={server.vultrInstanceId} server={server as ProvisionedServer} />
          ))}

        {/* Provisioning skeleton: active subscription but no servers provisioned yet */}
        {!isLoading && !hasServers && hasActiveSubscription && (
          <ProvisioningSkeletonCard tier={activeAccess!.tier} />
        )}
      </CardContent>
    </Card>
  );
}
